"use client";

import { SetStateAction, useCallback, useLayoutEffect, useRef } from "react";

/**
 * Returns an accessor function that returns the latest value passed to the hook.
 *
 * The value is only updated after the component has rendered, so it is safe to use in event handlers.
 * This is useful for preventing unnecessary re-renders when a value changes frequently,
 * while still providing access to the latest value in event handlers.
 */
export function useLateReference<T>(value: T): () => T {
  const valueRef = useRef(value);

  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);

  return useCallback(() => valueRef.current, []);
}

export function useLateReferenceState<T>(
  value: T,
  setValue: (value: T) => void
): [() => T, (value: SetStateAction<T>) => void] {
  const valueRef = useRef(value);

  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);

  const getValue = useCallback(() => valueRef.current, []);

  const actualSetValue = useCallback(
    (value: SetStateAction<T>) => {
      if (value instanceof Function) {
        const newValue = value(valueRef.current);
        valueRef.current = newValue;
        setValue(newValue);
      } else {
        valueRef.current = value;
        setValue(value);
      }
    },
    [setValue]
  );

  return [getValue, actualSetValue] as const;
}
